import { z } from "zod";
import { optionalNumber } from "./_helpers/optional";

// ADR-0052: a trade partner is a role on the unified contact model, not a
// standalone entity. These schemas mirror the customer party model
// (CustomerAttributesSchema / CustomerUnifiedSchema in contact.schema.ts).

// === Trade Partner sub-roles ===

export const tradePartnerRoles = [
  "supplier",
  "manufacturer",
  "distributor",
  "warranty_provider",
  "financing_provider",
  "subcontractor",
] as const;
export type TradePartnerRoleType = (typeof tradePartnerRoles)[number];

// === core.trade_partner_attributes ===

export const TradePartnerAttributesSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  partner_number: z.string().nullable().describe("Partner Number"),
  payment_terms: z.string().nullable().describe("Payment Terms"),
  default_lead_time_days: z
    .number()
    .int()
    .nullable()
    .describe("Default Lead Time Days"),
  website: z.string().nullable().describe("Website"),
  status: z.string().nullable().describe("Status"),
  notes: z.string().nullable().describe("Notes"),
  tags: z.array(z.string()).nullable().describe("Tags"),
  custom_fields: z.record(z.unknown()).nullable().describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type TradePartnerAttributes = z.infer<
  typeof TradePartnerAttributesSchema
>;

// === core.trade_partner_roles ===

export const TradePartnerRoleSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  partner_role: z.enum(tradePartnerRoles).describe("Partner Role"),
  created_at: z.string().describe("Created At"),
});
export type TradePartnerRole = z.infer<typeof TradePartnerRoleSchema>;

// === core_api.trade_partners_unified (+ per-sub-role views) ===
//
// The per-sub-role views (manufacturers, distributors, subcontractors,
// financing_providers, warranty_providers) project the identical column shape —
// they are the umbrella view filtered by trade_partner_roles — so they reuse
// this schema rather than redeclaring it.

export const TradePartnerUnifiedSchema = z.object({
  contact_id: z.string().uuid().describe("Contact"),
  email: z.string().email().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  company_name: z.string().nullable().describe("Company Name"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  is_active: z.boolean().describe("Active"),
  trade_partner_attributes_id: z
    .string()
    .uuid()
    .describe("Trade Partner Attributes"),
  partner_number: z.string().nullable().describe("Partner Number"),
  payment_terms: z.string().nullable().describe("Payment Terms"),
  default_lead_time_days: z
    .number()
    .int()
    .nullable()
    .describe("Default Lead Time Days"),
  website: z.string().nullable().describe("Website"),
  status: z.string().nullable().describe("Status"),
  notes: z.string().nullable().describe("Notes"),
  tags: z.array(z.string()).nullable().describe("Tags"),
  custom_fields: z.record(z.unknown()).nullable().describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type TradePartnerUnified = z.infer<typeof TradePartnerUnifiedSchema>;

// The per-sub-role views (core_api.manufacturers / distributors /
// subcontractors / financing_providers / warranty_providers) are the umbrella
// view filtered by trade_partner_roles — an identical column projection. Parse
// their rows with TradePartnerUnifiedSchema; these aliases give callers a
// semantic per-role name without forking a second identical schema object
// (which knip flags as a duplicate export).
export type Manufacturer = TradePartnerUnified;
export type Distributor = TradePartnerUnified;
export type Subcontractor = TradePartnerUnified;
export type FinancingProvider = TradePartnerUnified;
export type WarrantyProvider = TradePartnerUnified;

// === core.product_distributor_sources ===

export const ProductDistributorSourceSchema = z.object({
  id: z.string().uuid().describe("ID"),
  product_id: z.string().uuid().describe("Product"),
  trade_partner_contact_role_id: z
    .string()
    .uuid()
    .describe("Distributor Trade Partner"),
  distributor_sku: z.string().nullable().describe("Distributor SKU"),
  list_cost: z.number().nullable().describe("List Cost"),
  lead_time_days: z.number().int().nullable().describe("Lead Time Days"),
  is_preferred: z.boolean().describe("Preferred"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type ProductDistributorSource = z.infer<
  typeof ProductDistributorSourceSchema
>;

export const ProductDistributorSourceInsertSchema = z.object({
  product_id: z.string().uuid().describe("Product"),
  trade_partner_contact_role_id: z
    .string()
    .uuid()
    .describe("Distributor Trade Partner"),
  distributor_sku: z.string().optional().describe("Distributor SKU"),
  list_cost: optionalNumber().describe("List Cost"),
  lead_time_days: z.number().int().optional().describe("Lead Time Days"),
  is_preferred: z.boolean().optional().describe("Preferred"),
});

export const ProductDistributorSourceUpdateSchema =
  ProductDistributorSourceInsertSchema.partial();

// === Trade partner write schemas (attributes table inserts) ===

export const TradePartnerAttributesInsertSchema = z.object({
  partner_number: z.string().optional().describe("Partner Number"),
  payment_terms: z.string().optional().describe("Payment Terms"),
  default_lead_time_days: z
    .number()
    .int()
    .optional()
    .describe("Default Lead Time Days"),
  website: z.string().optional().describe("Website"),
  status: z.string().optional().describe("Status"),
  notes: z.string().optional().describe("Notes"),
  tags: z.array(z.string()).optional().describe("Tags"),
  custom_fields: z.any().optional().describe("Custom Fields"),
});

export const TradePartnerAttributesUpdateSchema =
  TradePartnerAttributesInsertSchema.partial();
